import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faTrash, faEdit, faFileAlt, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import { getFolderDisplayLabel } from '../../utils/folderHierarchy';
import { useAccount } from '../../contexts/AccountContext';
import { getUserData } from '../../utils/userUtils';
import { isSuperAdmin } from '../../utils/permissions';
import { useChannelConfigs } from '../../hooks/useChannelConfigs';
import './FrequenceTrackingPage.css';

const STATUS_COLORS = {
  received: '#6b7280',
  validating: '#f59e0b',
  submitting: '#3b82f6',
  processing: '#8b5cf6',
  retrying: '#f59e0b',
  done: '#10b981',
  error: '#ef4444',
};

const EMPTY_PARTNER = {
  partner_id: '',
  partner_name: '',
  madhive_advertiser_id: '',
  madhive_channel_id: '',
  // Cross-channel file drops: campaign-sem-*.json launches Google Ads search
  // via sem_channel_id; campaign-social-*.json / campaign-fb-*.json launches
  // Facebook via fb_channel_id. Blank = the scheduler auto-resolves the
  // account's enabled channel of that type ('google-ads-search' / 'facebook').
  sem_channel_id: '',
  fb_channel_id: '',
  folder_id: '',
  gcs_base_path: 'default',
};

// Seat -> channel-config mapping row (Seat Config tab). When a Frequence file
// drop carries a MadHive seat_id (advertiser.seat_id on creates,
// madhive_seat_id on edits/creatives), the pipeline uses the mapped channel's
// credentials for that request. This table is the single source of truth,
// maintained ONLY from this tab — drops naming an unmapped or paused seat
// FAIL validation. A drop with a BLANK seat_id uses the reserved mapping row
// whose seat ID is the literal string "default" when one exists (otherwise
// the current channel behavior).
const EMPTY_SEAT_CONFIG = {
  seat_id: '',
  seat_name: '',
  madhive_channel_id: '',
  // Channel used when a drop payload carries top-level staging:true. A staging
  // drop whose seat mapping has no staging channel FAILS validation (it never
  // falls back to the production channel).
  staging_madhive_channel_id: '',
};

const FILES_PAGE_SIZE = 10;

const fileSize = (bytes) => (bytes > 0 ? `${(bytes / 1024).toFixed(1)} KB` : '-');

/**
 * Renders a single directory's files with pagination, filtered by name and
 * sorted newest first.
 */
const FilesDirectory = ({ partnerId, dir, fileList, filterText, onClearAll, onDownload, formatDate, canClear }) => {
  const [page, setPage] = useState(0);

  // Reset to the first page whenever the filter changes.
  useEffect(() => { setPage(0); }, [filterText]);

  const q = (filterText || '').trim().toLowerCase();
  const filtered = q
    ? fileList.filter(f => (f.name || '').toLowerCase().includes(q))
    : fileList;

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.updated || 0).getTime() - new Date(a.updated || 0).getTime()
  );

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / FILES_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * FILES_PAGE_SIZE;
  const pageItems = sorted.slice(start, start + FILES_PAGE_SIZE);

  const countLabel = total === fileList.length
    ? `(${fileList.length})`
    : `(${total} of ${fileList.length})`;

  return (
    <div className="files-dir-section">
      <h4 className="files-dir-header">
        {dir}/ <span className="files-dir-count">{countLabel}</span>
        {fileList.length > 0 && canClear && (
          <button className="files-clear-btn" onClick={() => onClearAll(partnerId, dir)}>
            Clear All
          </button>
        )}
      </h4>
      {fileList.length === 0 ? (
        <div className="files-empty">empty</div>
      ) : total === 0 ? (
        <div className="files-empty">no files match "{filterText}"</div>
      ) : (
        <>
          <table className="frequence-table">
            <thead>
              <tr>
                <th style={{ minWidth: '300px' }}>File</th>
                <th style={{ width: '120px' }}>Size</th>
                <th style={{ width: '180px' }}>Updated ↓</th>
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(file => (
                <tr key={file.full_path}>
                  <td>{file.name}</td>
                  <td>{fileSize(file.size)}</td>
                  <td className="date-cell">{formatDate(file.updated)}</td>
                  <td>
                    <button className="files-download-btn" onClick={() => onDownload(file.full_path, file.name)}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="frequence-pagination">
              <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Previous</button>
              <span>{start + 1}-{Math.min(start + FILES_PAGE_SIZE, total)} of {total}</span>
              <button disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FrequenceTrackingPage = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;

  const [workflows, setWorkflows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    operationType: '',
    partnerId: '',
    fileName: '',
    limit: 25,
    offset: 0,
  });
  // Debounced text bound to the Workflows file-name filter input, so we don't
  // fire a request per keystroke.
  const [wfFileInput, setWfFileInput] = useState('');
  const [activeTab, setActiveTab] = useState('workflows');
  const [partners, setPartners] = useState([]);
  const [files, setFiles] = useState({});
  const [filesActivePartner, setFilesActivePartner] = useState('');
  // Client-side name filter for the Files tab (applies to the selected partner).
  const [fileFilter, setFileFilter] = useState('');
  const [filesLoading, setFilesLoading] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [seatConfigs, setSeatConfigs] = useState([]);
  const [seatChannels, setSeatChannels] = useState([]);
  const [editingSeatConfig, setEditingSeatConfig] = useState(null);
  const [seatSaving, setSeatSaving] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});
  const [logModal, setLogModal] = useState({ open: false, loading: false, data: null, error: null, filename: '' });
  const [folders, setFolders] = useState([]);
  const { getChannelsForPlatform } = useChannelConfigs(accountId, user);
  const madhiveChannels = getChannelsForPlatform('madhive');
  // Channel dropdowns for cross-channel file drops (SEM / Social). Keys match
  // channel_types.name, which is how useChannelConfigs buckets configs.
  const semChannels = getChannelsForPlatform('google-ads-search');
  const facebookChannels = getChannelsForPlatform('facebook');

  const fetchWorkflows = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({ accountId, userId });
      if (filters.status) params.append('status', filters.status);
      if (filters.operationType) params.append('operationType', filters.operationType);
      if (filters.partnerId) params.append('partnerId', filters.partnerId);
      if (filters.fileName) params.append('fileName', filters.fileName);
      params.append('limit', filters.limit);
      params.append('offset', filters.offset);

      const response = await api.get(getApiUrl(`/api/v2/frequence/workflows?${params}`));
      setWorkflows(response.data.workflows || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching workflows:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId, userId, filters]);

  const fetchDashboard = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/frequence/dashboard?accountId=${accountId}&userId=${userId}`));
      setDashboard(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }, [accountId, userId]);

  const fetchFolders = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: { accountId, userId, limit: 100 }
      });
      setFolders(response.data.folders || response.data || []);
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  }, [accountId, userId]);

  const fetchPartners = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/frequence/partners?accountId=${accountId}&userId=${userId}`));
      setPartners(response.data.partners || []);
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  }, [accountId, userId]);

  const fetchSeatConfigs = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/frequence/seat-configs?accountId=${accountId}&userId=${userId}`));
      setSeatConfigs(response.data.seatConfigs || []);
    } catch (err) {
      console.error('Error fetching seat configs:', err);
    }
  }, [accountId, userId]);

  // ALL MadHive-platform channel configs under the account for the seat-form
  // dropdown. Uses the dedicated frequence endpoint (super-admin bypass,
  // includes disabled account_channels rows) rather than the user-scoped
  // useChannelConfigs hook, which can return a subset.
  const fetchSeatChannels = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/frequence/madhive-channels?accountId=${accountId}&userId=${userId}`));
      setSeatChannels(response.data.channels || []);
    } catch (err) {
      console.error('Error fetching madhive channels:', err);
    }
  }, [accountId, userId]);

  const fetchFiles = useCallback(async () => {
    if (!accountId) return;
    try {
      setFilesLoading(true);
      const response = await api.get(getApiUrl(`/api/v2/frequence/files?accountId=${accountId}&userId=${userId}`));
      setFiles(response.data.partners || {});
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setFilesLoading(false);
    }
  }, [accountId, userId]);

  const handleDownloadFile = async (filePath, fileName) => {
    try {
      const response = await api.get(
        getApiUrl(`/api/v2/frequence/files/download?accountId=${accountId}&userId=${userId}&filePath=${encodeURIComponent(filePath)}`)
      );
      const url = response.data.url;
      if (url) {
        // Open the signed URL in a new tab — the browser will prompt to save
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleClearDirectory = async (partnerId, directory) => {
    if (!window.confirm(`Delete ALL files in ${partnerId}/${directory}/? This cannot be undone.`)) return;
    try {
      const response = await api.delete(
        getApiUrl(`/api/v2/frequence/files/${partnerId}/${directory}?accountId=${accountId}&userId=${userId}`)
      );
      console.log(`Deleted ${response.data.deleted} files`);
      fetchFiles();
    } catch (err) {
      console.error('Error clearing directory:', err);
      alert('Failed to clear directory: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchWorkflows();
  }, [fetchDashboard, fetchWorkflows]);

  useEffect(() => {
    if (activeTab === 'partners') { fetchPartners(); fetchFolders(); }
    if (activeTab === 'files') { fetchFiles(); fetchPartners(); }
    if (activeTab === 'workflows') fetchPartners();  // for the partner filter dropdown
    if (activeTab === 'seats') { fetchSeatConfigs(); fetchSeatChannels(); }
  }, [activeTab, fetchPartners, fetchFolders, fetchFiles, fetchSeatConfigs, fetchSeatChannels]);

  // Default the Files sub-tab to the first partner, and keep it valid as files load.
  useEffect(() => {
    const keys = Object.keys(files);
    if (keys.length && !keys.includes(filesActivePartner)) {
      setFilesActivePartner(keys[0]);
    }
  }, [files, filesActivePartner]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchWorkflows();
      fetchDashboard();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchWorkflows, fetchDashboard]);

  // Debounce the Workflows file-name filter → commit to filters (resets paging).
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => (prev.fileName === wfFileInput ? prev : { ...prev, fileName: wfFileInput, offset: 0 }));
    }, 400);
    return () => clearTimeout(t);
  }, [wfFileInput]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // ========================================================================
  // Partner CRUD
  // ========================================================================

  // Dropdown toggle (three-dot menu)
  const toggleDropdown = (id, e) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 160;
    let left = rect.right - dropdownWidth;
    let top = rect.bottom + 5;
    if (left < 10) left = 10;
    if (top + 120 > window.innerHeight) top = rect.top - 120;
    setDropdownPosition({ top, left });
    setOpenDropdownId(id);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdownId && !Object.values(dropdownRefs.current).some(ref => ref?.contains(e.target))) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  const handleViewLog = async (workflow) => {
    setLogModal({ open: true, loading: true, data: null, error: null, filename: workflow.frequence_workflow_id || '' });
    try {
      const response = await api.get(getApiUrl(`/api/v2/frequence/workflows/${workflow.id}/log?accountId=${accountId}&userId=${userId}`));
      setLogModal(prev => ({ ...prev, loading: false, data: response.data }));
    } catch (err) {
      setLogModal(prev => ({ ...prev, loading: false, error: err.response?.data?.error || 'Failed to load log' }));
    }
  };

  const handleDeleteWorkflow = async (workflow) => {
    if (!window.confirm(`Delete workflow "${workflow.frequence_workflow_id}"?`)) return;
    try {
      await api.delete(getApiUrl(`/api/v2/frequence/workflows/${workflow.id}?accountId=${accountId}&userId=${userId}`));
      fetchWorkflows();
      fetchDashboard();
    } catch (err) {
      console.error('Error deleting workflow:', err);
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCreatePartner = () => {
    setEditingPartner({ ...EMPTY_PARTNER, _isNew: true });
  };

  const handleEditPartner = (partner) => {
    setEditingPartner({ ...partner, _isNew: false });
  };

  const handleCancelPartner = () => {
    setEditingPartner(null);
  };

  const handleSavePartner = async () => {
    if (!editingPartner) return;
    setPartnerSaving(true);
    try {
      if (editingPartner._isNew) {
        await api.post(getApiUrl('/api/v2/frequence/partners'), {
          accountId,
          userId,
          partner_id: editingPartner.partner_id,
          partner_name: editingPartner.partner_name,
          madhive_advertiser_id: editingPartner.madhive_advertiser_id,
          madhive_channel_id: editingPartner.madhive_channel_id || undefined,
          sem_channel_id: editingPartner.sem_channel_id || undefined,
          fb_channel_id: editingPartner.fb_channel_id || undefined,
          folder_id: editingPartner.folder_id || undefined,
          gcs_base_path: editingPartner.gcs_base_path || 'default',
        });
      } else {
        await api.put(getApiUrl(`/api/v2/frequence/partners/${editingPartner.id}`), {
          accountId,
          userId,
          partner_name: editingPartner.partner_name,
          madhive_advertiser_id: editingPartner.madhive_advertiser_id,
          madhive_channel_id: editingPartner.madhive_channel_id || undefined,
          // null (not undefined) so clearing the dropdown reverts the partner
          // to auto-resolve — undefined is skipped by the update allowlist.
          sem_channel_id: editingPartner.sem_channel_id || null,
          fb_channel_id: editingPartner.fb_channel_id || null,
          folder_id: editingPartner.folder_id || undefined,
          gcs_base_path: editingPartner.gcs_base_path || 'default',
          is_active: editingPartner.is_active,
        });
      }
      setEditingPartner(null);
      fetchPartners();
    } catch (err) {
      console.error('Error saving partner:', err);
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setPartnerSaving(false);
    }
  };

  const handleDeletePartner = async (partner) => {
    if (!window.confirm(`Delete partner "${partner.partner_name}"? It will be removed from this list and its GCS path will no longer be monitored.`)) return;
    try {
      await api.delete(getApiUrl(`/api/v2/frequence/partners/${partner.id}?accountId=${accountId}&userId=${userId}`));
      fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  // Pause (is_active=false) stops the scheduler from polling this partner's
  // GCS bucket; Activate (is_active=true) resumes it. Files and workflows for
  // the partner remain visible either way.
  const handleTogglePartnerActive = async (partner) => {
    const pausing = partner.is_active;
    try {
      await api.put(getApiUrl(`/api/v2/frequence/partners/${partner.id}`), {
        accountId, userId, is_active: !partner.is_active,
      });
      fetchPartners();
    } catch (err) {
      console.error('Error toggling partner active state:', err);
      alert(`Failed to ${pausing ? 'pause' : 'activate'} partner: ` + (err.response?.data?.error || err.message));
    }
  };

  const handlePartnerField = (field, value) => {
    setEditingPartner(prev => ({ ...prev, [field]: value }));
  };

  // ========================================================================
  // Seat -> Channel Config CRUD (mirrors the partner-config handlers)
  // ========================================================================

  const handleCreateSeatConfig = () => {
    setEditingSeatConfig({ ...EMPTY_SEAT_CONFIG, _isNew: true });
  };

  const handleEditSeatConfig = (seatConfig) => {
    setEditingSeatConfig({ ...seatConfig, _isNew: false });
  };

  const handleCancelSeatConfig = () => {
    setEditingSeatConfig(null);
  };

  const handleSaveSeatConfig = async () => {
    if (!editingSeatConfig) return;
    setSeatSaving(true);
    try {
      if (editingSeatConfig._isNew) {
        await api.post(getApiUrl('/api/v2/frequence/seat-configs'), {
          accountId,
          userId,
          seat_id: editingSeatConfig.seat_id,
          seat_name: editingSeatConfig.seat_name || undefined,
          madhive_channel_id: editingSeatConfig.madhive_channel_id || undefined,
          staging_madhive_channel_id: editingSeatConfig.staging_madhive_channel_id || undefined,
        });
      } else {
        await api.put(getApiUrl(`/api/v2/frequence/seat-configs/${editingSeatConfig.id}`), {
          accountId,
          userId,
          seat_name: editingSeatConfig.seat_name || null,
          madhive_channel_id: editingSeatConfig.madhive_channel_id || null,
          staging_madhive_channel_id: editingSeatConfig.staging_madhive_channel_id || null,
          is_active: editingSeatConfig.is_active,
        });
      }
      setEditingSeatConfig(null);
      fetchSeatConfigs();
    } catch (err) {
      console.error('Error saving seat config:', err);
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSeatSaving(false);
    }
  };

  const handleDeleteSeatConfig = async (seatConfig) => {
    if (!window.confirm(`Delete seat mapping "${seatConfig.seat_id}"? Drops naming this seat will FAIL validation until the seat is re-added here.`)) return;
    try {
      await api.delete(getApiUrl(`/api/v2/frequence/seat-configs/${seatConfig.id}?accountId=${accountId}&userId=${userId}`));
      fetchSeatConfigs();
    } catch (err) {
      console.error('Error deleting seat config:', err);
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  // Pause (is_active=false) disables the seat entirely — drops naming this
  // seat FAIL validation until the mapping is re-activated. Activate resumes
  // processing under the mapped channel.
  const handleToggleSeatConfigActive = async (seatConfig) => {
    const pausing = seatConfig.is_active;
    try {
      await api.put(getApiUrl(`/api/v2/frequence/seat-configs/${seatConfig.id}`), {
        accountId, userId, is_active: !seatConfig.is_active,
      });
      fetchSeatConfigs();
    } catch (err) {
      console.error('Error toggling seat config active state:', err);
      alert(`Failed to ${pausing ? 'pause' : 'activate'} seat config: ` + (err.response?.data?.error || err.message));
    }
  };

  const handleSeatConfigField = (field, value) => {
    setEditingSeatConfig(prev => ({ ...prev, [field]: value }));
  };

  // Display name for a partner_id, falling back to the id when the config
  // isn't loaded (e.g. files exist for a partner not in the fetched list).
  const partnerLabel = (partnerId) =>
    partners.find(p => p.partner_id === partnerId)?.partner_name || partnerId;

  return (
    <div className="frequence-tracking-page">
      <div className="frequence-header">
        <h1>Frequence Integration</h1>
        <p className="frequence-subtitle">File-based campaign operations with Madhive</p>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="frequence-stats">
          <div className="stat-card">
            <div className="stat-value">{dashboard.total || 0}</div>
            <div className="stat-label">Total Operations</div>
          </div>
          <div className="stat-card stat-processing">
            <div className="stat-value">{(dashboard.by_status?.received || 0) + (dashboard.by_status?.validating || 0) + (dashboard.by_status?.submitting || 0) + (dashboard.by_status?.processing || 0) + (dashboard.by_status?.retrying || 0)}</div>
            <div className="stat-label">Processing</div>
          </div>
          <div className="stat-card stat-done">
            <div className="stat-value">{dashboard.by_status?.done || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card stat-error">
            <div className="stat-value">{dashboard.by_status?.error || 0}</div>
            <div className="stat-label">Errors</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="frequence-tabs">
        <button className={`tab-btn ${activeTab === 'workflows' ? 'active' : ''}`} onClick={() => setActiveTab('workflows')}>
          Workflows
        </button>
        <button className={`tab-btn ${activeTab === 'files' ? 'active' : ''}`} onClick={() => setActiveTab('files')}>
          Files
        </button>
        <button className={`tab-btn ${activeTab === 'partners' ? 'active' : ''}`} onClick={() => setActiveTab('partners')}>
          Partner Config
        </button>
        <button className={`tab-btn ${activeTab === 'seats' ? 'active' : ''}`} onClick={() => setActiveTab('seats')}>
          Seat Config
        </button>
      </div>

      {/* ================================================================ */}
      {/* Workflows Tab */}
      {/* ================================================================ */}
      {activeTab === 'workflows' && (
        <div className="frequence-section">
          <div className="frequence-filters">
            <select value={filters.status} onChange={e => setFilters(prev => ({ ...prev, status: e.target.value, offset: 0 }))}>
              <option value="">All Statuses</option>
              <option value="received">Received</option>
              <option value="validating">Validating</option>
              <option value="submitting">Submitting</option>
              <option value="processing">Processing</option>
              <option value="retrying">Retrying</option>
              <option value="done">Done</option>
              <option value="error">Error</option>
            </select>
            <select value={filters.operationType} onChange={e => setFilters(prev => ({ ...prev, operationType: e.target.value, offset: 0 }))}>
              <option value="">All Types</option>
              <option value="CREATE">Create</option>
              <option value="CREATIVE">Creative</option>
              <option value="EDIT">Edit</option>
            </select>
            <select value={filters.partnerId} onChange={e => setFilters(prev => ({ ...prev, partnerId: e.target.value, offset: 0 }))}>
              <option value="">All Partners</option>
              {partners.map(p => (
                <option key={p.id} value={p.partner_id}>{p.partner_name}</option>
              ))}
            </select>
            <input
              className="frequence-filter-input"
              type="text"
              placeholder="Filter by file name..."
              value={wfFileInput}
              onChange={e => setWfFileInput(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="frequence-loading">Loading...</div>
          ) : (
            <>
              <div className="frequence-table-scroll">
              <table className="frequence-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Type</th>
                    <th style={{ width: '160px' }}>Partner</th>
                    <th style={{ minWidth: '380px' }}>File</th>
                    <th style={{ width: '160px' }}>Status</th>
                    <th style={{ width: '80px' }}>Attempt</th>
                    <th style={{ width: '180px' }}>Next retry</th>
                    <th style={{ width: '180px' }}>Created</th>
                    <th style={{ minWidth: '300px' }}>Error</th>
                    <th style={{ width: '70px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {workflows.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="frequence-empty">
                        No workflows found. Files placed in the GCS bucket will appear here.
                      </td>
                    </tr>
                  ) : (
                    workflows.map(w => (
                      <tr key={w.id} className={`workflow-row status-${w.status}`}>
                        <td><span className="operation-badge">{w.operation_type}</span></td>
                        <td className="partner-cell">{w.partner_name || w.partner_id || '-'}</td>
                        <td>
                          {w.campaign_id ? (
                            <span className="campaign-link" onClick={() => navigate(`/campaigns/${w.campaign_id}`)}>
                              {w.frequence_workflow_id || '-'}
                            </span>
                          ) : (w.frequence_workflow_id || '-')}
                        </td>
                        <td>
                          <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[w.status] || '#6b7280' }}>
                            {w.status}
                          </span>
                        </td>
                        <td>{w.retry_count > 0 ? `${w.retry_count} / ${w.max_retries ?? 3}` : '—'}</td>
                        <td className="date-cell">{w.status === 'retrying' && w.next_retry_at ? formatDate(w.next_retry_at) : '—'}</td>
                        <td className="date-cell">{formatDate(w.created_at)}</td>
                        <td className="error-cell">
                          {w.error_message ? (
                            <span className="error-text" title={w.error_message}>
                              {w.error_message}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="actions-cell">
                          <div className="dropdown-container" ref={el => { dropdownRefs.current[`wf-${w.id}`] = el; }}>
                            <button className="sidebar-action-button always-visible" onClick={e => toggleDropdown(`wf-${w.id}`, e)}>
                              <div className="action-button-content"><FontAwesomeIcon icon={faEllipsisV} /></div>
                            </button>
                            {openDropdownId === `wf-${w.id}` && (
                              <div className="sidebar-dropdown-menu" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }} onClick={e => e.stopPropagation()}>
                                <button className="sidebar-dropdown-item" onMouseDown={() => { setOpenDropdownId(null); handleViewLog(w); }}>
                                  <FontAwesomeIcon icon={faFileAlt} /> View Log
                                </button>
                                <button className="sidebar-dropdown-item delete" onMouseDown={() => { setOpenDropdownId(null); handleDeleteWorkflow(w); }}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>

              {/* Pagination */}
              <div className="frequence-pagination">
                <button
                  disabled={filters.offset === 0}
                  onClick={() => setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                >
                  Previous
                </button>
                <span>
                  {total === 0 ? 'No results' : `${filters.offset + 1}-${Math.min(filters.offset + filters.limit, total)} of ${total}`}
                </span>
                <button
                  disabled={filters.offset + filters.limit >= total}
                  onClick={() => setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Files Tab */}
      {/* ================================================================ */}
      {activeTab === 'files' && (
        <div className="frequence-section">
          <div className="files-toolbar">
            <input
              className="frequence-filter-input files-filter-input"
              type="text"
              placeholder="Filter by file name..."
              value={fileFilter}
              onChange={e => setFileFilter(e.target.value)}
            />
            <button className="files-refresh-btn" onClick={fetchFiles} disabled={filesLoading}>
              {filesLoading ? 'Loading...' : '↻ Refresh'}
            </button>
          </div>
          {Object.keys(files).length === 0 && !filesLoading && (
            <div className="frequence-empty" style={{ padding: '40px', textAlign: 'center' }}>
              No partners configured. Add a partner config first.
            </div>
          )}
          {Object.keys(files).length > 0 && (
            <>
              {/* One sub-tab per partner; show only the selected partner's files */}
              <div className="frequence-tabs files-partner-tabs">
                {Object.keys(files).map(partnerId => (
                  <button
                    key={partnerId}
                    className={`tab-btn ${filesActivePartner === partnerId ? 'active' : ''}`}
                    onClick={() => setFilesActivePartner(partnerId)}
                  >
                    {partnerLabel(partnerId)}
                  </button>
                ))}
              </div>
              {filesActivePartner && files[filesActivePartner] && (
                <div className="files-partner-section">
                  {Object.entries(files[filesActivePartner]).map(([dir, fileList]) => (
                    <FilesDirectory
                      key={dir}
                      partnerId={filesActivePartner}
                      dir={dir}
                      fileList={fileList}
                      filterText={fileFilter}
                      onClearAll={handleClearDirectory}
                      onDownload={handleDownloadFile}
                      formatDate={formatDate}
                      canClear={isSuperAdmin(user) || !!user?.isImpersonating}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Partners Tab */}
      {/* ================================================================ */}
      {activeTab === 'partners' && (
        <div className="frequence-section">
          <div className="partner-toolbar">
            <button className="partner-add-btn" onClick={handleCreatePartner}>+ Add Partner</button>
          </div>

          {/* Partner Form (inline create/edit) */}
          {editingPartner && (
            <div className="partner-form">
              <h3>{editingPartner._isNew ? 'New Partner Config' : 'Edit Partner Config'}</h3>
              <div className="partner-form-grid">
                <label>
                  Partner ID *
                  <input
                    value={editingPartner.partner_id}
                    onChange={e => handlePartnerField('partner_id', e.target.value)}
                    disabled={!editingPartner._isNew}
                    placeholder="e.g. default"
                  />
                </label>
                <label>
                  Partner Name *
                  <input
                    value={editingPartner.partner_name}
                    onChange={e => handlePartnerField('partner_name', e.target.value)}
                    placeholder="e.g. Default Partner"
                  />
                </label>
                <label>
                  Madhive Advertiser ID *
                  <input
                    value={editingPartner.madhive_advertiser_id}
                    onChange={e => handlePartnerField('madhive_advertiser_id', e.target.value)}
                    placeholder="28-char Madhive ID"
                  />
                </label>
                <label>
                  Madhive Channel Config
                  <select
                    value={editingPartner.madhive_channel_id || ''}
                    onChange={e => handlePartnerField('madhive_channel_id', e.target.value)}
                  >
                    <option value="">Select channel config...</option>
                    {madhiveChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>{ch.name || ch.id}</option>
                    ))}
                  </select>
                </label>
                <label>
                  SEM Channel Config (Google Ads)
                  <select
                    value={editingPartner.sem_channel_id || ''}
                    onChange={e => handlePartnerField('sem_channel_id', e.target.value)}
                  >
                    <option value="">Auto (account's google-ads-search channel)</option>
                    {semChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>{ch.name || ch.id}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Social Channel Config (Facebook)
                  <select
                    value={editingPartner.fb_channel_id || ''}
                    onChange={e => handlePartnerField('fb_channel_id', e.target.value)}
                  >
                    <option value="">Auto (account's facebook channel)</option>
                    {facebookChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>{ch.name || ch.id}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Folder
                  <select
                    value={editingPartner.folder_id || ''}
                    onChange={e => handlePartnerField('folder_id', e.target.value)}
                  >
                    <option value="">Select folder...</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{getFolderDisplayLabel(f)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  GCS Base Path
                  <input
                    value={editingPartner.gcs_base_path}
                    onChange={e => handlePartnerField('gcs_base_path', e.target.value)}
                    placeholder="default"
                  />
                </label>
                {!editingPartner._isNew && (
                  <label>
                    Active
                    <select
                      value={editingPartner.is_active ? 'true' : 'false'}
                      onChange={e => handlePartnerField('is_active', e.target.value === 'true')}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="partner-form-actions">
                <button className="partner-save-btn" onClick={handleSavePartner} disabled={partnerSaving}>
                  {partnerSaving ? 'Saving...' : 'Save'}
                </button>
                <button className="partner-cancel-btn" onClick={handleCancelPartner} disabled={partnerSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <table className="frequence-table">
            <thead>
              <tr>
                <th>Partner ID</th>
                <th>Name</th>
                <th>Madhive Advertiser</th>
                <th>SEM / FB Channels</th>
                <th>GCS Path</th>
                <th>Active</th>
                <th>Created</th>
                <th style={{ width: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr>
                  <td colSpan="8" className="frequence-empty">
                    No partner configs yet. Click "+ Add Partner" to create one.
                  </td>
                </tr>
              ) : (
                partners.map(p => (
                  <tr key={p.id}>
                    <td>{p.partner_id}</td>
                    <td>{p.partner_name}</td>
                    <td className="mono-text">{p.madhive_advertiser_id}</td>
                    <td>
                      {`SEM: ${p.sem_channel_name || (p.sem_channel_id ? p.sem_channel_id : 'auto')}`}
                      <br />
                      {`FB: ${p.fb_channel_name || (p.fb_channel_id ? p.fb_channel_id : 'auto')}`}
                    </td>
                    <td>{p.gcs_base_path}</td>
                    <td><span className={`active-badge ${p.is_active ? 'active' : 'inactive'}`}>{p.is_active ? 'Yes' : 'No'}</span></td>
                    <td className="date-cell">{formatDate(p.created_at)}</td>
                    <td className="actions-cell">
                      <div className="dropdown-container" ref={el => { dropdownRefs.current[`pt-${p.id}`] = el; }}>
                        <button className="sidebar-action-button always-visible" onClick={e => toggleDropdown(`pt-${p.id}`, e)}>
                          <div className="action-button-content"><FontAwesomeIcon icon={faEllipsisV} /></div>
                        </button>
                        {openDropdownId === `pt-${p.id}` && (
                          <div className="sidebar-dropdown-menu" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }} onClick={e => e.stopPropagation()}>
                            <button className="sidebar-dropdown-item" onMouseDown={() => { setOpenDropdownId(null); handleEditPartner(p); }}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <button className="sidebar-dropdown-item" onMouseDown={() => { setOpenDropdownId(null); handleTogglePartnerActive(p); }}>
                              <FontAwesomeIcon icon={p.is_active ? faPause : faPlay} /> {p.is_active ? 'Pause' : 'Activate'}
                            </button>
                            <button className="sidebar-dropdown-item delete" onMouseDown={() => { setOpenDropdownId(null); handleDeletePartner(p); }}>
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ================================================================ */}
      {/* Seat Config Tab — seat_id -> MadHive channel-config mapping      */}
      {/* ================================================================ */}
      {activeTab === 'seats' && (
        <div className="frequence-section">
          <div className="partner-toolbar">
            <button className="partner-add-btn" onClick={handleCreateSeatConfig}>+ Add Seat Mapping</button>
          </div>

          {/* Seat Config Form (inline create/edit) */}
          {editingSeatConfig && (
            <div className="partner-form">
              <h3>{editingSeatConfig._isNew ? 'New Seat Mapping' : 'Edit Seat Mapping'}</h3>
              <div className="partner-form-grid">
                <label>
                  Seat ID *
                  <input
                    value={editingSeatConfig.seat_id}
                    onChange={e => handleSeatConfigField('seat_id', e.target.value)}
                    disabled={!editingSeatConfig._isNew}
                    placeholder={'28-char MadHive seat ID, or "default"'}
                    title={'Use the reserved seat ID "default" to map drops that carry NO seat_id'}
                  />
                </label>
                <label>
                  Seat Name
                  <input
                    value={editingSeatConfig.seat_name || ''}
                    onChange={e => handleSeatConfigField('seat_name', e.target.value)}
                    placeholder="e.g. Nexstar"
                  />
                </label>
                <label>
                  Madhive Channel Config
                  <select
                    value={editingSeatConfig.madhive_channel_id || ''}
                    onChange={e => handleSeatConfigField('madhive_channel_id', e.target.value)}
                  >
                    <option value="">Select channel config...</option>
                    {seatChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>
                        {(ch.name || ch.id) + (ch.is_enabled === false ? ' (disabled)' : '')}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Staging Channel Config
                  <select
                    value={editingSeatConfig.staging_madhive_channel_id || ''}
                    onChange={e => handleSeatConfigField('staging_madhive_channel_id', e.target.value)}
                    title={'Used when a drop payload carries top-level "staging": true. Staging drops FAIL validation if this is not set — they never fall back to the production channel.'}
                  >
                    <option value="">None — staging drops fail</option>
                    {seatChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>
                        {(ch.name || ch.id) + (ch.is_enabled === false ? ' (disabled)' : '')}
                      </option>
                    ))}
                  </select>
                </label>
                {!editingSeatConfig._isNew && (
                  <label>
                    Active
                    <select
                      value={editingSeatConfig.is_active ? 'true' : 'false'}
                      onChange={e => handleSeatConfigField('is_active', e.target.value === 'true')}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="partner-form-actions">
                <button className="partner-save-btn" onClick={handleSaveSeatConfig} disabled={seatSaving}>
                  {seatSaving ? 'Saving...' : 'Save'}
                </button>
                <button className="partner-cancel-btn" onClick={handleCancelSeatConfig} disabled={seatSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <table className="frequence-table">
            <thead>
              <tr>
                <th>Seat ID</th>
                <th>Name</th>
                <th>Channel Config</th>
                <th>Staging Channel Config</th>
                <th>Active</th>
                <th>Created</th>
                <th style={{ width: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {seatConfigs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="frequence-empty">
                    No seat mappings yet. Click "+ Add Seat Mapping" to create one.
                    Drops naming a seat_id that isn't mapped here fail validation.
                    Use the reserved seat ID "default" to map drops that carry no seat_id.
                  </td>
                </tr>
              ) : (
                seatConfigs.map(s => (
                  <tr key={s.id}>
                    <td className="mono-text">{s.seat_id}</td>
                    <td>{s.seat_name || '-'}</td>
                    <td>{s.channel_name || (s.madhive_channel_id ? s.madhive_channel_id : <em>none — uses current channel</em>)}</td>
                    <td>{s.staging_channel_name || (s.staging_madhive_channel_id ? s.staging_madhive_channel_id : <em>none — staging drops fail</em>)}</td>
                    <td><span className={`active-badge ${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Yes' : 'No'}</span></td>
                    <td className="date-cell">{formatDate(s.created_at)}</td>
                    <td className="actions-cell">
                      <div className="dropdown-container" ref={el => { dropdownRefs.current[`sc-${s.id}`] = el; }}>
                        <button className="sidebar-action-button always-visible" onClick={e => toggleDropdown(`sc-${s.id}`, e)}>
                          <div className="action-button-content"><FontAwesomeIcon icon={faEllipsisV} /></div>
                        </button>
                        {openDropdownId === `sc-${s.id}` && (
                          <div className="sidebar-dropdown-menu" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }} onClick={e => e.stopPropagation()}>
                            <button className="sidebar-dropdown-item" onMouseDown={() => { setOpenDropdownId(null); handleEditSeatConfig(s); }}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <button className="sidebar-dropdown-item" onMouseDown={() => { setOpenDropdownId(null); handleToggleSeatConfigActive(s); }}>
                              <FontAwesomeIcon icon={s.is_active ? faPause : faPlay} /> {s.is_active ? 'Pause' : 'Activate'}
                            </button>
                            <button className="sidebar-dropdown-item delete" onMouseDown={() => { setOpenDropdownId(null); handleDeleteSeatConfig(s); }}>
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Log Modal */}
      {logModal.open && (
        <div className="log-modal-overlay" onClick={() => setLogModal({ open: false, loading: false, data: null, error: null, filename: '' })}>
          <div className="log-modal" onClick={e => e.stopPropagation()}>
            <div className="log-modal-header">
              <h3>{logModal.filename}</h3>
              <button className="log-modal-close" onClick={() => setLogModal({ open: false, loading: false, data: null, error: null, filename: '' })}>
                &times;
              </button>
            </div>
            <div className="log-modal-body">
              {logModal.loading && <div className="log-modal-loading">Loading log...</div>}
              {logModal.error && <div className="log-modal-error">{logModal.error}</div>}
              {logModal.data && logModal.data.entries && (
                <div className="log-entries">
                  {logModal.data.entries.map((entry, idx) => (
                    <div key={idx} className={`log-entry ${entry.message?.startsWith('ERROR') ? 'log-entry-error' : ''} ${entry.message?.startsWith('Status: DONE') ? 'log-entry-done' : ''}`}>
                      <span className="log-timestamp">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                      <span className="log-message">{entry.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {logModal.data && !logModal.data.entries && (
                <pre className="log-raw">{JSON.stringify(logModal.data, null, 2)}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrequenceTrackingPage;
